/* Header behavior: hide-on-scroll-down, native <dialog> mobile menu.
   Desktop mega-menu is CSS-only (:focus-within / :hover); JS adds only its assistive
   state (#247): aria-expanded follows hover/focus, Escape closes the open panel. */

import { bus } from './critical.js';

class StickyHeader extends HTMLElement {
  connectedCallback() {
    // #260 (2026-09-09, Codex F21): publish --header-height for the sticky elements that stack
    // under the nav (the collection pill strip, sections/main-collection.liquid; the PDP gallery,
    // snippets/product-gallery.liquid). Measured from THIS element — the nav's own box — because
    // the <header> landmark in theme.liquid is display:contents (#105: only the nav sticks), so
    // its offsetHeight is always 0 and the old wrapper measurement published 0px on every page.
    // The utility bar above is not sticky and must not be counted. While hide-on-scroll
    // translates the nav away the value is 0, so those consumers slide up to the viewport edge
    // (the pill strip transitions `top`) and back down when the nav returns.
    this.setHeaderHeight = () => {
      const height = this.classList.contains('header-hidden') ? 0 : this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };
    this.setHeaderHeight();
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(this.setHeaderHeight);
      this.ro.observe(this);
    }

    // #260 (2026-09-09, Codex F31): the handler lives on the instance so disconnectedCallback can
    // remove it — a theme-editor section re-render replaces this element and connects a new one.
    let lastY = window.scrollY;
    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!this.isConnected) return; /* frame queued before the editor swapped this element out */
        const y = window.scrollY;
        const goingDown = y > lastY && y > this.offsetHeight;
        const changed = this.classList.contains('header-hidden') !== goingDown;
        this.classList.toggle('header-hidden', goingDown);
        if (changed) this.setHeaderHeight();
        lastY = y;
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.ro?.disconnect();
  }
}

class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    wireMenuOpeners();
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) return this.dialog.close();
      /* #371 (2026-09-16): every link tap closes the menu BEFORE the browser navigates, so the
         page iOS Safari / Chrome keep in the back-forward cache is snapshotted with the dialog
         already closed — pressing Back used to land behind an open menu with the page still
         scroll-locked (the lock is CSS: html:has(.mobile-menu-dialog[open]) in
         styles/tailwind.css, so closing releases it). Delegated, so links inside the accordions
         and the promo/social/CTA rows are all covered. */
      const link = event.target.closest && event.target.closest('a[href]');
      if (link && this.dialog.contains(link)) this.dialog.close();
    });
    for (const closer of this.querySelectorAll('[data-menu-close]')) {
      closer.addEventListener('click', () => this.dialog.close());
    }
    /* #371: belt and braces for a page that was cached WITH the menu open (a tap that was not a
       link, a restore from a different browser's cache): on a back-forward restore close it and
       the scroll lock goes with it. Listener on the instance so disconnectedCallback removes it
       (#260, Codex F31: the theme editor re-renders this section). */
    this.onPageShow = (event) => {
      if (event.persisted && this.dialog.open) this.dialog.close();
    };
    window.addEventListener('pageshow', this.onPageShow);
    // #260 (2026-09-09, Codex F31): bus.on returns its unsubscribe (assets/critical.js); kept so a
    // theme-editor re-render of this section does not leave the old element's listener behind.
    this.offDrawerOpen = bus.on('drawer:open', () => this.dialog.open && this.dialog.close());
  }

  disconnectedCallback() {
    this.offDrawerOpen?.();
    this.offDrawerOpen = null;
    window.removeEventListener('pageshow', this.onPageShow);
  }
}

/* #260 (2026-09-09, Codex F31): the burger ([data-menu-open], sections/header.liquid) is wired once
   per trigger — same shape as wireDrawerTriggers in assets/cart.js — so re-running MobileMenu's
   connectedCallback after a theme-editor re-render no longer stacks click listeners (each stale one
   called showModal on a <dialog> that was already gone). The dialog is looked up per click, not
   captured, so a trigger wired before the menu section re-rendered opens the new <dialog>; a header
   re-render brings new triggers, which the shopify:section:load listener below wires. */
function wireMenuOpeners(root = document) {
  for (const trigger of root.querySelectorAll('[data-menu-open]')) {
    if (trigger.dataset.menuWired) continue;
    trigger.dataset.menuWired = 'true';
    trigger.addEventListener('click', () => {
      const dialog = document.querySelector('mobile-menu dialog');
      if (dialog && !dialog.open) dialog.showModal();
    });
  }
}

/* #247 (2026-09-07): the CSS-only mega menu tells assistive tech when a panel is open and closes
   on Escape. Each trigger with aria-haspopup sits in a `.group` <li> (the theme block's wrapper)
   next to its [data-mega-panel]; `mega-closed` on that <li> hides the panel (styles/tailwind.css,
   unlayered) until the pointer leaves or focus moves elsewhere. */
function wireMegaMenu(root = document) {
  for (const trigger of root.querySelectorAll('[data-mega-trigger][aria-haspopup]')) {
    const item = trigger.closest('li');
    if (!item || item.dataset.megaWired) continue;
    item.dataset.megaWired = '1';
    const set = (open) => trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.addEventListener('mouseenter', () => { item.classList.remove('mega-closed'); set(true); });
    item.addEventListener('mouseleave', () => { item.classList.remove('mega-closed'); set(item.contains(document.activeElement)); });
    item.addEventListener('focusin', () => { if (!item.classList.contains('mega-closed')) set(true); });
    item.addEventListener('focusout', (event) => {
      if (item.contains(event.relatedTarget)) return;
      item.classList.remove('mega-closed');
      set(false);
    });
  }
}
/* Escape is handled at document level: a hover-only user has focus elsewhere, so the open
   item never sees the key. Close every open panel; return focus to its trigger only when
   focus was inside that item. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  for (const trigger of document.querySelectorAll('[data-mega-trigger][aria-expanded="true"]')) {
    const item = trigger.closest('li');
    if (!item) continue;
    item.classList.add('mega-closed');
    trigger.setAttribute('aria-expanded', 'false');
    if (item.contains(document.activeElement)) trigger.focus();
  }
});
wireMegaMenu();
document.addEventListener('shopify:section:load', (event) => {
  wireMegaMenu(event.target);
  wireMenuOpeners(event.target); /* #260 (Codex F31): a re-rendered header brings a new burger */
});

if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
if (!customElements.get('mobile-menu')) customElements.define('mobile-menu', MobileMenu);
